import { alpha, Box, Card, CardContent, Typography } from "@mui/material";
import { grey } from "@mui/material/colors";
import LanguageSwitcher from "components/LanguageSwitcher";
import { Logo } from "components/Logo";
import { useRef, type FC, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

interface AuthCardWrapperProps {
    title: string;
    description: string;
    children: ReactNode;
}

const AuthCardWrapper: FC<AuthCardWrapperProps> = ({ children, description, title }) => {

    const { t, i18n } = useTranslation();

    const refBgIndex = useRef<number>(1);

    return (
        <Box sx={{ display: "flex", width: "100vw", height: "100vh" }}>
            <Box
                sx={{
                    flex: 1,
                    position: "relative",
                    overflow: "hidden",
                    display: { xs: "none", md: "block" },
                }}
            >
                <Box
                    component="img"
                    src={`/images/${refBgIndex.current}.jpg`}
                    alt="auth-bg"
                    sx={{
                        position: "absolute",
                        inset: 0,
                        objectFit: "cover",
                        objectPosition: "center",
                    }}
                />
                <Box
                    sx={{
                        position: "absolute",
                        inset: 0,
                        backgroundImage: `linear-gradient(0deg, ${alpha("#000", 0.8)} 8%,transparent 100%)`,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "end",
                        fontSize: "3rem",
                        padding: { xs: "2rem", md: "4rem" },
                        color: (theme) => theme.palette.secondary.contrastText,
                    }}
                >
                    <Typography variant="h4" sx={{ fontSize: { xs: "2.5rem", md: "3rem" } }}>
                        Kampux
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{
                            textAlign: "center",
                            color: grey[400],
                            fontSize: { xs: "0.9rem", md: "1rem" },
                        }}
                    >
                        {t('welcomeDescription')}
                    </Typography>
                </Box>
            </Box>
            <Box
                sx={{
                    flex: { xs: 1, md: 1 },
                    width: { xs: "100%", md: "auto" },
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                <Box
                    sx={{
                        p: 2,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                    }}
                >
                    <Logo />
                    <LanguageSwitcher />
                </Box>
                <Box
                    sx={{
                        flex: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <Card
                        sx={{
                            boxShadow: "none",
                            width: { xs: "90%", sm: "80%", md: "75%", lg: "60%" },
                            borderRadius: 2,
                        }}
                    >
                        <CardContent sx={{ p: { xs: 3, sm: 4, md: 5 } }}>
                            <Box>
                                <Typography
                                    variant="h5"
                                    sx={{
                                        fontWeight: "bold",
                                        mb: 1,
                                    }}
                                >
                                    {title}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: grey[700],
                                        fontSize: { xs: "0.875rem", sm: "1rem" },
                                    }}
                                >
                                    {description}
                                </Typography>
                            </Box>
                            <Box mt={{ xs: 3, sm: 4 }} />
                            {children}
                        </CardContent>
                    </Card>
                </Box>
                <Box
                    sx={{
                        p: 2,
                        textAlign: "center",
                        color: "text.secondary",
                        fontSize: "0.9rem",
                    }}
                    component="footer"
                >
                    © Copyright {new Date().getFullYear()} Powered by{" "}
                    <Typography
                        component={Link}
                        to="#"
                        sx={{
                            color: (theme) => theme.palette.primary.main,
                        }}
                    >
                        Univsoft
                    </Typography>
                </Box>
            </Box>
        </Box>



    );
};

export default AuthCardWrapper;
